import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Card, Checkbox, Grid, Typography } from "@mui/material";
import ActionCell from "components/personnelAndFleet/common/ActionCell";
import ChevronButton from "components/personnelAndFleet/common/ChevronButton";
import DetailCell from "components/personnelAndFleet/common/DetailCell";
import StatusCell from "components/personnelAndFleet/common/StatusCell";
import { FLEET_STATUS_LABELS, FleetVehicleStatus } from "enums/FleetEnums";
import { FleetVehicle } from "models/FleetVehicle";
import { getFleetVehicleActionItems } from "./fleetUtils";

function getRefrigeratedLabel(value: string | boolean) {
	if (typeof value === "boolean") {
		return value ? "Yes" : "No";
	}

	if (String(value).toLowerCase() === "yes") {
		return "Yes";
	}

	if (String(value).toLowerCase() === "no") {
		return "No";
	}

	return "-";
}

type FleetCardListProps = {
	rows: FleetVehicle[];
	selectedRows: Set<number>;
	setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>;
	selectionDisabled?: boolean;
	onEditVehicle: (vehicle: FleetVehicle) => void;
	onDuplicateVehicle: (vehicle: FleetVehicle) => void;
	onDeactivateVehicle: (vehicle: FleetVehicle) => void;
	onActivateVehicle: (vehicle: FleetVehicle) => void;
	canWrite?: boolean;
};

export default function FleetCardList({
	rows,
	selectedRows,
	setSelectedRows,
	selectionDisabled = false,
	onEditVehicle,
	onDuplicateVehicle,
	onDeactivateVehicle,
	onActivateVehicle,
	canWrite = false
}: FleetCardListProps) {
	const handleToggleRow = (rowId: number) => {
		setSelectedRows((prev) => {
			const next = new Set(prev);
			next.has(rowId) ? next.delete(rowId) : next.add(rowId);
			return next;
		});
	};

	if (rows.length === 0) {
		return (
			<Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
				<Typography variant="text14">No fleet vehicles found.</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				flex: 1,
				minHeight: 0,
				overflowY: "auto",
				gap: 1.5,
				backgroundColor: "common.white",
				p: 2
			}}
		>
			{rows.map((row) => {
				const isSelected = selectedRows.has(row.id);
				const actionItems = getFleetVehicleActionItems(row, {
					onEditVehicle,
					onDuplicateVehicle,
					onDeactivateVehicle,
					onActivateVehicle
				});

				return (
					<Card
						key={row.id}
						variant="outlined"
						sx={{
							display: "flex",
							flexShrink: 0,
							flexDirection: { xs: "column", sm: "row" },
							alignItems: { xs: "stretch", sm: "center" },
							gap: "7px",
							p: 2,
							pl: { sm: "7px" },
							borderWidth: 2,
							borderColor: isSelected ? "text.primary" : "border.hover",
							borderRadius: 1.5,
							boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.1)"
						}}
					>
						{/* Checkbox — visible on sm+ (left side) */}
						<Box sx={{ display: { xs: "none", sm: "flex" } }}>
							<Checkbox
								checked={isSelected}
								onChange={() => handleToggleRow(row.id)}
								disabled={selectionDisabled}
							/>
						</Box>

						{/* Rows wrapper — stacks registration above details */}
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 1.5,
								flex: "1 0 0",
								minWidth: 0
							}}
						>
							{/* Row 1: Registration (+ checkbox on xs) */}
							<Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5 }}>
								<Typography variant="text16Bold" sx={{ flex: "1 0 0", minWidth: 0 }}>
									{row.registration || "-"}
								</Typography>

								{/* Checkbox — visible on xs only (top-right) */}
								<Box sx={{ display: { xs: "flex", sm: "none" }, mt: -1.5, mr: -1.5 }}>
									<Checkbox
										checked={isSelected}
										onChange={() => handleToggleRow(row.id)}
										disabled={selectionDisabled}
									/>
								</Box>
							</Box>

							{/* Row 2: Details (+ chevron on xs only) */}
							<Box sx={{ display: "flex", gap: { xs: 1.25, sm: 1.5 }, minWidth: 0 }}>
								<Grid container spacing={{ xs: 0.5, sm: 1.5 }} sx={{ flex: "1 0 0", minWidth: 0 }}>
									<DetailCell label="Vehicle Type" value={row.type || "-"} />
									<DetailCell
										label="Length (m)"
										value={row.lengthM != null ? String(row.lengthM) : "-"}
									/>
									<DetailCell label="Default Driver">
											<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
												<Typography
													variant="text14"
													color={row.driverActive === false ? "text.disabled" : "inherit"}
												>
													{row.defaultDriver || "-"}
												</Typography>
												{row.driverActive === false && (
													<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
														<WarningAmberIcon fontSize="small" color="warning" />
														<Typography variant="text12" color="warning.main">
															Deactivated
														</Typography>
													</Box>
											)}
										</Box>
									</DetailCell>
									<DetailCell label="Refrigerated" value={getRefrigeratedLabel(row.refrigerated)} />
									<DetailCell label="Status">
										<StatusCell
											isActive={row.status === FleetVehicleStatus.ACTIVE}
											label={
												row.status
													? (FLEET_STATUS_LABELS[row.status as FleetVehicleStatus] ?? row.status)
													: "-"
											}
										/>
									</DetailCell>
								</Grid>

								{/* Chevron — visible on xs only (next to details) */}
								{canWrite && (
									<ActionCell
										items={actionItems}
										trigger={(onClick) => <ChevronButton onClick={onClick} />}
										sx={{ display: { xs: "flex", sm: "none" }, mr: -1.5 }}
									/>
								)}
							</Box>
						</Box>

						{/* Chevron — visible on sm+ (card level, full height) */}
						{canWrite && (
							<ActionCell
								items={actionItems}
								trigger={(onClick) => <ChevronButton onClick={onClick} />}
								sx={{ display: { xs: "none", sm: "flex" }, alignSelf: "stretch", mr: -1.5 }}
							/>
						)}
					</Card>
				);
			})}
		</Box>
	);
}
